import type { MenuAction } from "./MenuInput.js";

/**
 * ScreenManager — the arcade shell's stack router
 * (docs/menu-redesign-plan.md §4.1).
 *
 * One source of truth for which shell overlay is visible: exactly one screen
 * is *current* (top of the stack) at any time; everything else is hidden.
 * Screens are plain objects implementing {@link ArcadeScreen} — no framework.
 *
 * Class contract applied to every screen's root element (styled in tokens.css):
 *   `ui-screen`     always
 *   `hidden`        while off (global `display:none !important` rule)
 *   `is-active`     while top of stack
 *   `is-entering`   flicker-in, removed after `transitionMs` (≤ 250 ms)
 *   `is-exiting`    fade-out, root goes `hidden` after `exitMs`
 *
 * DOM access is confined to `classList` on the roots plus `host.appendChild`
 * at register time, so the whole router is testable in the node vitest
 * environment with fake elements.
 */

export interface ArcadeScreen {
  /** Called once, lazily, before the first enter. Build your DOM under `root`. */
  mount(root: HTMLElement): void;
  /** Screen became current. `params` come from show/push/pop callers. */
  onEnter(params?: unknown): void;
  /** Screen stopped being current (covered, replaced or popped). */
  onLeave(): void;
  /**
   * Unified menu input (keyboard + gamepad via MenuInput). Return `true` to
   * consume — consumed keyboard actions get preventDefault'd upstream.
   */
  handleInput?(action: MenuAction): boolean | void;
}

export interface ScreenManagerOptions {
  /** Container the screen roots are appended to (e.g. `#arcade-root`). */
  host: Pick<HTMLElement, "appendChild">;
  /** Root factory — override in tests. Default: `<div id="screen-<name>" class="ui-screen hidden">`. */
  makeRoot?: (name: string) => HTMLElement;
  /** Enter flicker duration in ms. Default 200 (plan: never slower than 250). */
  transitionMs?: number;
  /** Exit fade duration in ms. Default 120. */
  exitMs?: number;
  /** Fires whenever the current screen changes; `null` = shell fully hidden. */
  onActiveChange?: (name: string | null) => void;
}

interface Entry {
  name: string;
  screen: ArcadeScreen;
  root: HTMLElement;
  mounted: boolean;
  enterTimer: ReturnType<typeof setTimeout> | null;
  exitTimer: ReturnType<typeof setTimeout> | null;
}

const CLS_HIDDEN = "hidden";
const CLS_ACTIVE = "is-active";
const CLS_ENTER = "is-entering";
const CLS_EXIT = "is-exiting";

function defaultMakeRoot(name: string): HTMLElement {
  const el = document.createElement("div");
  el.id = `screen-${name}`;
  el.className = `ui-screen ${CLS_HIDDEN}`;
  return el;
}

export class ScreenManager {
  private readonly entries = new Map<string, Entry>();
  private stackNames: string[] = [];
  private readonly host: Pick<HTMLElement, "appendChild">;
  private readonly makeRoot: (name: string) => HTMLElement;
  private readonly transitionMs: number;
  private readonly exitMs: number;
  private readonly onActiveChange: ((name: string | null) => void) | null;

  constructor(opts: ScreenManagerOptions) {
    this.host = opts.host;
    this.makeRoot = opts.makeRoot ?? defaultMakeRoot;
    this.transitionMs = opts.transitionMs ?? 200;
    this.exitMs = opts.exitMs ?? 120;
    this.onActiveChange = opts.onActiveChange ?? null;
  }

  register(name: string, screen: ArcadeScreen): void {
    if (this.entries.has(name)) {
      console.warn(`[ScreenManager] "${name}" registered twice — ignoring`);
      return;
    }
    const root = this.makeRoot(name);
    this.host.appendChild(root);
    this.entries.set(name, { name, screen, root, mounted: false, enterTimer: null, exitTimer: null });
  }

  has(name: string): boolean {
    return this.entries.has(name);
  }

  /** Name of the current (top) screen, or null when the shell is hidden. */
  current(): string | null {
    return this.stackNames.length > 0 ? this.stackNames[this.stackNames.length - 1]! : null;
  }

  /** Copy of the stack, bottom → top. */
  stack(): string[] {
    return [...this.stackNames];
  }

  rootOf(name: string): HTMLElement | null {
    return this.entries.get(name)?.root ?? null;
  }

  /**
   * Reset the stack to `[name]` and make it current.
   * Showing the screen that is already current re-runs its `onEnter(params)`
   * (no onLeave, no transition classes) — used to re-sync bridge screens.
   */
  show(name: string, params?: unknown): void {
    const entry = this.entries.get(name);
    if (!entry) {
      console.warn(`[ScreenManager] show("${name}") — unknown screen`);
      return;
    }
    if (this.current() === name) {
      entry.screen.onEnter(params);
      return;
    }
    const cur = this.currentEntry();
    if (cur) this.deactivate(cur);
    this.stackNames = [name];
    this.activate(entry, params);
  }

  /** Overlay `name` on top of the stack (previous screen leaves but is not forgotten). */
  push(name: string, params?: unknown): void {
    const entry = this.entries.get(name);
    if (!entry) {
      console.warn(`[ScreenManager] push("${name}") — unknown screen`);
      return;
    }
    if (this.current() === name) {
      entry.screen.onEnter(params);
      return;
    }
    const cur = this.currentEntry();
    if (cur) this.deactivate(cur);
    this.stackNames.push(name);
    this.activate(entry, params);
  }

  /**
   * Remove the top screen and re-enter the one below it (with `params`).
   * Returns false — and does nothing — when there is nothing to go back to
   * (stack size ≤ 1), so "back" can never empty the shell by accident.
   */
  pop(params?: unknown): boolean {
    if (this.stackNames.length <= 1) return false;
    const top = this.currentEntry();
    if (top) this.deactivate(top);
    this.stackNames.pop();
    const next = this.currentEntry();
    if (next) this.activate(next, params);
    return true;
  }

  /** In-game handoff: leave the current screen and empty the stack entirely. */
  hideAll(): void {
    const cur = this.currentEntry();
    if (cur) this.deactivate(cur);
    this.stackNames = [];
    this.onActiveChange?.(null);
  }

  /** Route a menu action to the current screen. Returns true if consumed. */
  dispatch(action: MenuAction): boolean {
    const cur = this.currentEntry();
    if (!cur || !cur.screen.handleInput) return false;
    return cur.screen.handleInput(action) === true;
  }

  // ── internals ───────────────────────────────────────────────────────────

  private currentEntry(): Entry | null {
    const name = this.current();
    return name !== null ? (this.entries.get(name) ?? null) : null;
  }

  private activate(entry: Entry, params?: unknown): void {
    // Cancel a still-running exit — the screen may be coming straight back.
    if (entry.exitTimer !== null) {
      clearTimeout(entry.exitTimer);
      entry.exitTimer = null;
    }
    entry.root.classList.remove(CLS_EXIT);

    if (!entry.mounted) {
      entry.mounted = true;
      entry.screen.mount(entry.root);
    }

    entry.root.classList.remove(CLS_HIDDEN);
    entry.root.classList.add(CLS_ACTIVE, CLS_ENTER);
    if (entry.enterTimer !== null) clearTimeout(entry.enterTimer);
    entry.enterTimer = setTimeout(() => {
      entry.enterTimer = null;
      entry.root.classList.remove(CLS_ENTER);
    }, this.transitionMs);

    entry.screen.onEnter(params);
    this.onActiveChange?.(entry.name);
  }

  private deactivate(entry: Entry): void {
    if (entry.enterTimer !== null) {
      clearTimeout(entry.enterTimer);
      entry.enterTimer = null;
    }
    entry.root.classList.remove(CLS_ENTER, CLS_ACTIVE);
    entry.screen.onLeave();

    if (this.exitMs <= 0) {
      entry.root.classList.add(CLS_HIDDEN);
      return;
    }
    entry.root.classList.add(CLS_EXIT);
    if (entry.exitTimer !== null) clearTimeout(entry.exitTimer);
    entry.exitTimer = setTimeout(() => {
      entry.exitTimer = null;
      entry.root.classList.remove(CLS_EXIT);
      entry.root.classList.add(CLS_HIDDEN);
    }, this.exitMs);
  }
}
